import { LoaderCircle } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cn } from '../../lib/cn';
import { buttonClass, type ButtonStyleProps } from './button-styles';

export interface ButtonProps extends ComponentProps<'button'>, ButtonStyleProps {
  loading?: boolean;
  loadingLabel?: string;
}

export function Button({
  variant,
  size,
  block,
  loading = false,
  loadingLabel,
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonClass({ variant, size, block }), className)}
      {...props}
    >
      {loading ? (
        <>
          <LoaderCircle className="animate-spin" aria-hidden />
          {loadingLabel ?? children}
        </>
      ) : (
        children
      )}
    </button>
  );
}

export function ButtonLink({
  variant,
  size,
  block,
  className,
  ...props
}: LinkProps & ButtonStyleProps) {
  return <Link className={cn(buttonClass({ variant, size, block }), className)} {...props} />;
}

interface IconButtonProps extends ComponentProps<'button'> {
  label: string;
  children: ReactNode;
  tone?: 'plain' | 'filled';
}

/** A square, thumb-sized button that shows only an icon; the label is for assistive technology. */
export function IconButton({
  label,
  children,
  tone = 'plain',
  className,
  ...props
}: IconButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-ink transition-[transform,background-color] active:scale-95 [&_svg]:size-5',
        tone === 'filled' ? 'bg-raised hover:brightness-95' : 'hover:bg-raised',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
